import React, { useMemo, useState } from "react";
import { Bell, Bookmark, ChevronDown, Search as SearchIcon, Trash2, X } from "lucide-react";

import { DATE_PRESETS, US_STATES } from "./TenderFilters";

// One line describing a saved search's alert setting, or none at all — the
// same wording whether it's on the card or (later) the management page.
const alertLabel = (row) => {
  if (!row.alert_email || row.alert_frequency === "off") return null;
  return row.alert_frequency === "weekly" ? "Weekly email" : "Daily email";
};

function SavedSearchCard({ row, active, onOpen, onDelete, onEditAlerts }) {
  const alert = alertLabel(row);
  return (
    <div
      className={`group relative border rounded-lg px-2.5 py-2 flex gap-2 items-start cursor-pointer transition-colors ${
        active ? "border-[#E3B8DE] bg-white shadow-[0_1px_2px_rgba(187,66,174,0.08)]" : "border-[#F1F1F3] bg-white hover:border-[#E5E7EB]"
      }`}
      onClick={() => onOpen(row)}
    >
      {active && <span className="w-[3px] self-stretch rounded shrink-0 bg-[#BB42AE]" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-outfit text-[13.5px] font-semibold text-[#18101C] truncate flex-1 min-w-0">
            {row.name}
          </span>
          {row.new_count > 0 && (
            <span className="font-outfit text-[11px] font-bold text-white bg-[#BB42AE] rounded-full px-[7px] shrink-0">
              {row.new_count} new
            </span>
          )}
        </div>
        <div className="mt-0.5 font-outfit text-[11.5px] text-[#9CA3AF]">
          {alert || "Alerts off"}
        </div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          aria-label={`Alert settings for "${row.name}"`}
          onClick={(e) => {
            e.stopPropagation();
            onEditAlerts(row);
          }}
          className="text-[#9CA3AF] hover:text-[#BB42AE] p-0.5"
        >
          <Bell className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          aria-label={`Delete saved search "${row.name}"`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(row);
          }}
          className="text-[#9CA3AF] hover:text-[#DC2626] p-0.5"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Pinned above everything else in the rail — the whole point is that it's
// the first thing anyone sees when they come back to this page.
function SavedSearchesBlock({
  savedSearches, savedSearchesLoading, activeSavedSearchId, filtersChanged,
  onOpenSavedSearch, onDeleteSavedSearch, onEditAlerts, onUpdateSearch, onManageClick,
}) {
  if (savedSearchesLoading) return null;

  return (
    <div className="bg-[#FCF8FC] border-b border-[#F1F1F3] px-[18px] pt-3.5 pb-3">
      <div className="flex items-center gap-2 mb-2.5">
        <span className="font-outfit text-[12px] font-semibold uppercase tracking-[0.06em] text-[#52525B] flex-1 whitespace-nowrap">
          Saved searches
        </span>
        {savedSearches.length > 0 && onManageClick && (
          <button
            type="button"
            onClick={onManageClick}
            className="font-outfit text-[11.5px] font-semibold text-[#BB42AE] hover:underline shrink-0"
          >
            Manage
          </button>
        )}
      </div>

      {savedSearches.length === 0 ? (
        <div className="border border-dashed border-[#E3B8DE] bg-[#FDF9FD] rounded-lg px-3 py-3.5 text-center">
          <p className="font-outfit text-[12px] font-semibold text-[#18101C]">No saved searches yet</p>
          <p className="font-outfit text-[11.5px] leading-[1.45] text-[#71717A] mt-1">
            Set your filters, then save them to come back to the same search later.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {savedSearches.map((row) => (
            <SavedSearchCard
              key={row.id}
              row={row}
              active={row.id === activeSavedSearchId}
              onOpen={onOpenSavedSearch}
              onDelete={onDeleteSavedSearch}
              onEditAlerts={onEditAlerts}
            />
          ))}
        </div>
      )}

      {activeSavedSearchId != null && filtersChanged && (
        <div className="flex items-center justify-between gap-2 pt-2.5 mt-1 border-t border-dashed border-[#EADCE8]">
          <span className="font-outfit text-[11.5px] text-[#9CA3AF]">Filters changed</span>
          <button
            type="button"
            onClick={onUpdateSearch}
            className="inline-flex items-center gap-1.5 font-outfit text-[12px] font-semibold text-white
                       bg-[#A04A97] rounded-md px-2.5 py-1 hover:bg-[#8f4188]"
          >
            <Bookmark className="w-3 h-3" />
            Update search
          </button>
        </div>
      )}
    </div>
  );
}

// The left filter rail. Accordions rather than the dropdown chips, so several
// facets and their counts can be read at once without opening anything.
//
// Counts come from the facets endpoint, which computes each list facet with its
// own filter lifted. Without that, picking one state makes every other state
// read zero, which is the moment the number actually matters.

// Closed unless it is Status or already holds a filter. Everything open at once
// buried Set-aside and Documents below the fold; a section arriving from a
// shared link with a filter in it still opens, so nothing applied is hidden.
function Section({ title, badge, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen || badge > 0);

  return (
    <div className="border-b border-[#F1F1F3] px-[18px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 pt-4 pb-2.5 text-left"
      >
        <span className="font-outfit text-[12px] font-semibold uppercase tracking-[0.06em] text-[#52525B] flex-1">
          {title}
        </span>
        {badge != null && badge > 0 && (
          <span className="font-outfit text-[10px] font-semibold text-white bg-[#BB42AE]
                           rounded-full px-1.5 leading-[15px]">
            {badge}
          </span>
        )}
        <ChevronDown
          className={`w-3.5 h-3.5 text-[#9CA3AF] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="pb-3 flex flex-col gap-[5px]">{children}</div>}
    </div>
  );
}

// Drawn rather than styled: a native control keeps the browser's own tick and
// its own idea of what 16px means, which is what made these read as someone
// else's checkboxes next to the design. The input stays for keyboard and
// screen readers, just visually hidden.
function Box({ type, checked }) {
  const shape = type === "radio" ? "rounded-full" : "rounded-[4px]";

  if (type === "radio") {
    return (
      <span
        aria-hidden
        className={`w-[16px] h-[16px] shrink-0 box-border ${shape} ${
          checked ? "border-[5px] border-[#8B55DD] bg-white" : "border-[1.5px] border-[#B3B3B3]"
        }`}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={`w-[18px] h-[18px] shrink-0 box-border ${shape} flex items-center justify-center ${
        checked ? "bg-[#8B55DD]" : "border-[1.5px] border-[#B3B3B3]"
      }`}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff"
             strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </span>
  );
}

function Row({ type = "checkbox", checked, label, count, onToggle, name }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer select-none">
      {/* The input sits exactly on top of the box it controls, transparent,
          rather than being parked off-screen by sr-only. Clicking the label
          focuses it, and the browser scrolls every scrollable ancestor to
          reveal whatever has focus: with the input clipped somewhere else that
          threw the industry list 625px down its own scroller and dragged the
          page header out of view with it. Over the box, there is nothing to
          scroll to. */}
      <span className="relative flex shrink-0">
        <input
          type={type}
          name={name}
          checked={checked}
          onChange={onToggle}
          className="peer absolute inset-0 w-full h-full m-0 opacity-0 cursor-pointer"
        />
        <span className="peer-focus-visible:ring-2 peer-focus-visible:ring-[#8B55DD] peer-focus-visible:ring-offset-1 rounded-[4px] flex">
          <Box type={type} checked={checked} />
        </span>
      </span>
      <span className="font-outfit text-[14px] font-medium text-[#1A1A1A] flex-1 truncate">
        {label}
      </span>
      {count != null && (
        <span className="font-outfit text-[12px] text-[#9CA3AF] tabular-nums">
          {count.toLocaleString()}
        </span>
      )}
    </label>
  );
}

const STATE_NAMES = Object.fromEntries(US_STATES);

export default function TenderRail({
  filters, onChange, facets, activeCount = 0, onClearAll,
  savedSearches = [], savedSearchesLoading = false, activeSavedSearchId = null,
  filtersChanged = false, onOpenSavedSearch, onDeleteSavedSearch, onEditAlerts,
  onUpdateSearch, onManageClick,
}) {
  const [stateQuery, setStateQuery] = useState("");
  const [showAllStates, setShowAllStates] = useState(false);
  const [buyerQuery, setBuyerQuery] = useState("");
  const [showAllBuyers, setShowAllBuyers] = useState(false);

  const set = (patch) => onChange({ ...filters, ...patch });

  const toggleIn = (key, value) => {
    const current = filters[key];
    set({
      [key]: current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    });
  };

  const options = facets?.counts?.options;
  const statusCounts = options?.status || {};
  const typeCounts = options?.notice_type || {};
  // Memoised on the facets themselves: `|| []` is a new array every render,
  // which would rebuild the lists below on every keystroke in the search box.
  const stateCounts = useMemo(() => options?.state || [], [options]);
  const noDeadline = facets?.counts?.no_deadline || 0;
  const closedOnly = filters.status === "closed";

  const noticeTypes = facets?.notice_types || [];
  const categories = useMemo(() => facets?.categories || [], [facets]);
  const categoryCounts = options?.category || {};

  // Parents in the taxonomy's own order, each with its children. A child whose
  // parent is switched off is dropped rather than floated to the top level,
  // where it would read as an industry in its own right.
  const industries = useMemo(() => {
    const parents = categories.filter((c) => !c.parent_slug);
    return parents.map((parent) => ({
      parent,
      children: categories.filter((c) => c.parent_slug === parent.slug),
    }));
  }, [categories]);

  // SAM.gov also publishes ISO subdivision codes for work abroad — DE-RP,
  // JP-42, GB-SFK. They are real places, but the filter only offers US codes,
  // so listing them as options would promise a filter that does not exist.
  const overseas = useMemo(
    () =>
      stateCounts
        .filter(({ code }) => !STATE_NAMES[code])
        .reduce((total, row) => total + row.count, 0),
    [stateCounts]
  );

  // Ordered by how many tenders each holds, so the useful ones are not buried
  // alphabetically. A state the user already picked stays visible even at zero.
  const states = useMemo(() => {
    const q = stateQuery.trim().toLowerCase();
    const rows = stateCounts
      .filter(({ code }) => STATE_NAMES[code])
      .map(({ code, count }) => ({ code, count, name: STATE_NAMES[code] }))
      .filter(({ code, name }) =>
        !q ? true : name.toLowerCase().includes(q) || code.toLowerCase() === q
      );
    const picked = filters.state
      .filter((code) => !rows.some((r) => r.code === code))
      .map((code) => ({ code, count: 0, name: STATE_NAMES[code] || code }));
    return [...picked, ...rows];
  }, [stateCounts, stateQuery, filters.state]);

  const visibleStates = showAllStates || stateQuery ? states : states.slice(0, 5);

  const buyerCounts = useMemo(() => options?.buyer || [], [options]);

  // Same shape as states: ordered by volume, a picked one never disappears
  // even at zero, and searching narrows without losing what is already on.
  const buyers = useMemo(() => {
    const q = buyerQuery.trim().toLowerCase();
    const rows = buyerCounts.filter(({ name }) => (!q ? true : name.toLowerCase().includes(q)));
    const picked = filters.buyer
      .filter((name) => !rows.some((r) => r.name === name))
      .map((name) => ({ name, count: 0 }));
    return [...picked, ...rows];
  }, [buyerCounts, buyerQuery, filters.buyer]);

  const visibleBuyers = showAllBuyers || buyerQuery ? buyers : buyers.slice(0, 5);

  return (
    <div>
      <SavedSearchesBlock
        savedSearches={savedSearches}
        savedSearchesLoading={savedSearchesLoading}
        activeSavedSearchId={activeSavedSearchId}
        filtersChanged={filtersChanged}
        onOpenSavedSearch={onOpenSavedSearch}
        onDeleteSavedSearch={onDeleteSavedSearch}
        onEditAlerts={onEditAlerts}
        onUpdateSearch={onUpdateSearch}
        onManageClick={onManageClick}
      />

      {/* Sits with what it clears, and stays put while the rail scrolls. In the
          results header it was across the screen from the filters, next to the
          rows it was about to change. Only appears when there is work to undo. */}
      {activeCount > 0 && (
        <div className="sticky top-0 z-10 bg-white border-b border-[#F1F1F3] px-[18px] py-2.5
                        flex items-center justify-between gap-2">
          <span className="font-outfit text-[12px] font-semibold uppercase tracking-[0.06em] text-[#52525B]">
            {activeCount} filter{activeCount === 1 ? "" : "s"} on
          </span>
          <button
            type="button"
            onClick={onClearAll}
            className="inline-flex items-center gap-1 font-outfit text-[12px] font-semibold
                       text-[#BB42AE] border border-[#F0D7ED] bg-[#FDF6FC] rounded-md px-2 py-1
                       hover:bg-[#F8ECF6] hover:border-[#E3B8DE] transition-colors"
          >
            <X className="w-3 h-3" strokeWidth={2.5} />
            Clear all
          </button>
        </div>
      )}

      <Section title="Status" defaultOpen>
        {[
          ["open", "Open", statusCounts.open],
          ["closed", "Closed", statusCounts.closed],
          ["all", "All", (statusCounts.open || 0) + (statusCounts.closed || 0)],
        ].map(([value, label, count]) => (
          <Row
            key={value}
            type="radio"
            name="tender-status"
            checked={filters.status === value}
            label={label}
            count={count}
            // Switching to Closed drops any closing-date filter with it.
            // Leaving it set would keep a filter in the URL and in the count
            // that cannot affect a single row.
            onToggle={() =>
              set(
                value === "closed"
                  ? { status: value, closing_within_days: null, require_deadline: false }
                  : { status: value }
              )
            }
          />
        ))}
      </Section>

      <Section title="Notice type" badge={filters.notice_type.length}>
        <div className="max-h-[220px] overflow-y-auto custom-scrollbar pr-1">
          {noticeTypes.map((t) => (
            <Row
              key={t.value}
              checked={filters.notice_type.includes(t.value)}
              label={t.label}
              count={typeCounts[t.value] || 0}
              onToggle={() => toggleIn("notice_type", t.value)}
            />
          ))}
        </div>
      </Section>

      {/* Two levels. "IT & Software" alone is 2,631 tenders, so a firm doing
          cloud work still had to read all of them; its children cut that to a
          few hundred. Children sit under their parent, indented, and a parent
          still matches everything inside it. */}
      <Section title="Industry" badge={filters.category.length}>
        <div className="max-h-[280px] overflow-y-auto custom-scrollbar pr-1 flex flex-col gap-[5px]">
          {industries.map(({ parent, children }) => (
            <div key={parent.slug} className="flex flex-col gap-[5px]">
              <Row
                checked={filters.category.includes(parent.slug)}
                label={parent.label}
                count={categoryCounts[parent.slug]}
                onToggle={() => toggleIn("category", parent.slug)}
              />
              {children.length > 0 && (
                <div className="pl-[18px] flex flex-col gap-[5px]">
                  {children.map((c) => (
                    <Row
                      key={c.slug}
                      checked={filters.category.includes(c.slug)}
                      label={c.label}
                      count={categoryCounts[c.slug]}
                      onToggle={() => toggleIn("category", c.slug)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Location" badge={filters.state.length}>
        <div className="relative mb-2">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={14} />
          <input
            type="text"
            value={stateQuery}
            onChange={(e) => setStateQuery(e.target.value)}
            placeholder="Find a state"
            aria-label="Find a state"
            className="w-full h-[30px] font-outfit text-[13px] pl-8 pr-2 border border-[#00000024]
                       rounded-md bg-white outline-none focus:border-[#C95EBE]
                       placeholder:text-[#9CA3AF]"
          />
        </div>

        {visibleStates.map(({ code, name, count }) => (
          <Row
            key={code}
            checked={filters.state.includes(code)}
            label={name}
            count={count}
            onToggle={() => toggleIn("state", code)}
          />
        ))}

        {/* An empty list under a search box reads as broken rather than as
            "nothing matched what you typed". */}
        {stateQuery && visibleStates.length === 0 && (
          <p className="font-outfit text-[12px] text-[#9CA3AF] py-1">
            No state matches &ldquo;{stateQuery}&rdquo;.
          </p>
        )}

        {!stateQuery && states.length > 5 && (
          <button
            type="button"
            onClick={() => setShowAllStates((v) => !v)}
            className="mt-1 font-outfit text-[12px] font-medium text-[#BB42AE] hover:underline"
          >
            {showAllStates ? "Show fewer" : `View all ${states.length}`}
          </button>
        )}

        {/* Said here rather than after the fact: most federal tenders publish no
            place of performance, so this filter is narrower than it looks. */}
        <p className="mt-2 font-outfit text-[11px] leading-[15px] text-[#9CA3AF]">
          Around 7 in 10 federal tenders publish no place of performance. This filter cannot
          match those.
          {overseas > 0 &&
            ` A further ${overseas.toLocaleString()} are outside the US and not listed here.`}
        </p>
      </Section>

      <Section title="Organization" badge={filters.buyer.length}>
        <div className="relative mb-2">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={14} />
          <input
            type="text"
            value={buyerQuery}
            onChange={(e) => setBuyerQuery(e.target.value)}
            placeholder="Find an agency"
            aria-label="Find an agency"
            className="w-full h-[30px] font-outfit text-[13px] pl-8 pr-2 border border-[#00000024]
                       rounded-md bg-white outline-none focus:border-[#C95EBE]
                       placeholder:text-[#9CA3AF]"
          />
        </div>

        {visibleBuyers.map(({ name, count }) => (
          <Row
            key={name}
            checked={filters.buyer.includes(name)}
            label={name}
            count={count}
            onToggle={() => toggleIn("buyer", name)}
          />
        ))}

        {buyerQuery && visibleBuyers.length === 0 && (
          <p className="font-outfit text-[12px] text-[#9CA3AF] py-1">
            No agency matches &ldquo;{buyerQuery}&rdquo;.
          </p>
        )}

        {!buyerQuery && buyers.length > 5 && (
          <button
            type="button"
            onClick={() => setShowAllBuyers((v) => !v)}
            className="mt-1 font-outfit text-[12px] font-medium text-[#BB42AE] hover:underline"
          >
            {showAllBuyers ? "Show fewer" : `View all ${buyers.length}`}
          </button>
        )}
      </Section>

      <Section title="Set-aside" defaultOpen={false}>
        <Row
          checked={filters.set_aside_only}
          label="Set-aside only"
          onToggle={() => set({ set_aside_only: !filters.set_aside_only })}
        />
      </Section>

      <Section title="Published date" badge={filters.posted_since_days ? 1 : 0}>
        {DATE_PRESETS.map((p) => (
          <Row
            key={p.days}
            type="radio"
            name="tender-posted"
            checked={filters.posted_since_days === p.days}
            label={p.label}
            onToggle={() =>
              set({ posted_since_days: filters.posted_since_days === p.days ? null : p.days })
            }
          />
        ))}
      </Section>

      {/* require_deadline travels with these, so "Closing in 7 days" means the
          same here as it does on the stat tile above. Without it the two read
          5,315 and 19,136 for the same words, because the underlying filter
          also keeps every tender whose deadline was never published. */}
      <Section title="Closing date" badge={filters.closing_within_days ? 1 : 0}>
        {/* Every closed tender's deadline is already behind us, so "closing
            within 7 days" is true of all 37,541 of them. The control used to
            apply and change nothing at all, which is worse than being unable
            to click it. */}
        {closedOnly ? (
          <p className="font-outfit text-[12px] text-[#9CA3AF] leading-[1.45] py-1">
            These tenders have already closed, so a closing date cannot narrow them.
            Switch Status to Open or All to use this.
          </p>
        ) : (
          [7, 14, 30, 90].map((days) => (
            <Row
              key={days}
              type="radio"
              name="tender-closing"
              checked={filters.closing_within_days === days}
              label={`Closing in ${days} days`}
              onToggle={() =>
                set(
                  filters.closing_within_days === days
                    ? { closing_within_days: null, require_deadline: false }
                    : { closing_within_days: days, require_deadline: true }
                )
              }
            />
          ))
        )}
        {!closedOnly && noDeadline > 0 && (
          <p className="font-outfit text-[11px] text-[#9CA3AF] leading-[1.45] pt-1">
            {noDeadline.toLocaleString()} tender{noDeadline === 1 ? "" : "s"} publish no closing
            date. A closing filter cannot match those.
          </p>
        )}
      </Section>

      <Section title="Documents">
        <Row
          checked={filters.has_documents}
          label="Has documents"
          onToggle={() => set({ has_documents: !filters.has_documents })}
        />
      </Section>
    </div>
  );
}
